import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Segment,
  Loader,
  Message,
  Icon,
  Table,
  Divider,
  Grid,
  Input,
  Header,
} from 'semantic-ui-react';

const API_URL = 'https://suap.uft.edu.br/api/uft/rsc-tae/memoriais-pos-validacao/';

const nivelBadgeColor = (nivel) => {
  if (!nivel) return 'secondary';
  const n = nivel.toUpperCase();
  if (n.includes('III') || n.includes('3')) return 'success';
  if (n.includes('II') || n.includes('2')) return 'info';
  if (n.includes('I') || n.includes('1')) return 'warning';
  return 'secondary';
};

const BRBadge = ({ color, children }) => (
  <span className={`br-tag ${color}`} style={{ fontSize: '0.75em', whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const SimpleAccordionItem = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #ccc' }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          background: open ? '#f0f4fb' : '#f8f8f8',
          border: 'none',
          borderLeft: `3px solid ${open ? '#1351b4' : '#ccc'}`,
          padding: '0.75rem 1rem',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontWeight: 600,
          fontSize: '1rem',
          color: '#1351b4',
          transition: 'all 0.2s',
        }}
      >
        <i
          className={`fas fa-angle-${open ? 'down' : 'right'}`}
          style={{ fontSize: '0.9rem', minWidth: '0.9rem' }}
          aria-hidden="true"
        />
        {title}
      </button>
      {open && (
        <div style={{ padding: '1rem 0', width: '100%' }}>
          {children}
        </div>
      )}
    </div>
  );
};

const MemorialContent = ({ item }) => {
  const { identificacao, requerimento, memorial: mem, conclusao } = item;

  return (
    <div style={{ padding: '0.5rem 0' }}>
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={12}>
            <Header sub color="grey" style={{ marginBottom: '0.5rem' }}>Identificação</Header>
            <Table basic="very" compact size="small">
              <Table.Body>
                <Table.Row>
                  <Table.Cell><strong>SIAPE</strong></Table.Cell>
                  <Table.Cell>{identificacao?.siape}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell><strong>Cargo</strong></Table.Cell>
                  <Table.Cell>{identificacao?.cargo}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell><strong>Lotação</strong></Table.Cell>
                  <Table.Cell>{identificacao?.lotacao}</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Grid.Column>
          <Grid.Column width={12}>
            <Header sub color="grey" style={{ marginBottom: '0.5rem' }}>Requerimento</Header>
            <Table basic="very" compact size="small">
              <Table.Body>
                <Table.Row>
                  <Table.Cell><strong>Nível RSC Pretendido</strong></Table.Cell>
                  <Table.Cell>
                    <BRBadge color={nivelBadgeColor(requerimento?.nivel_rsc_pretendido)}>
                      {requerimento?.nivel_rsc_pretendido}
                    </BRBadge>
                  </Table.Cell>
                </Table.Row>
                {requerimento?.processo_anterior_id && (
                  <Table.Row>
                    <Table.Cell><strong>Processo Anterior</strong></Table.Cell>
                    <Table.Cell>{requerimento.processo_anterior_id}</Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {mem?.introducao && (
        <>
          <Divider />
          <Header as="h4">Introdução</Header>
          <p style={{ textAlign: 'justify', lineHeight: '1.7', color: '#333' }}>{mem.introducao}</p>
        </>
      )}

      {mem?.requisitos?.length > 0 && (
        <>
          <Divider />
          <Header as="h4">Requisitos</Header>
          <div className="br-accordion">
            {mem.requisitos.map((req, idx) => (
              <SimpleAccordionItem key={idx} title={req.label}>
                {req.descricao && (
                  <p style={{ color: '#555', marginBottom: '1rem' }}>{req.descricao}</p>
                )}
                {req.itens?.length > 0 && (
                  <Table celled compact size="small" style={{ fontSize: '0.85em' }}>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Nº</Table.HeaderCell>
                        <Table.HeaderCell>Título</Table.HeaderCell>
                        <Table.HeaderCell>Qtd.</Table.HeaderCell>
                        <Table.HeaderCell>Unidade</Table.HeaderCell>
                        <Table.HeaderCell>Pts/Un.</Table.HeaderCell>
                        <Table.HeaderCell>Total</Table.HeaderCell>
                        <Table.HeaderCell>Documentos</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {req.itens.map((itm) => (
                        <Table.Row key={itm.numero}>
                          <Table.Cell>{itm.numero}</Table.Cell>
                          <Table.Cell>{itm.titulo}</Table.Cell>
                          <Table.Cell>{itm.quantidade}</Table.Cell>
                          <Table.Cell>{itm.unidade_medida}</Table.Cell>
                          <Table.Cell>{itm.pontuacao_unidade}</Table.Cell>
                          <Table.Cell><strong>{itm.pontuacao_total}</strong></Table.Cell>
                          <Table.Cell>
                            {itm.documentos?.map((doc, di) => (
                              <div key={di} style={{ color: '#444' }}>
                                <i className="fas fa-file-alt" style={{ marginRight: '0.3rem', fontSize: '0.8em' }} />
                                {doc.descricao}
                              </div>
                            ))}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                )}
              </SimpleAccordionItem>
            ))}
          </div>
        </>
      )}

      {conclusao && (
        <>
          <Divider />
          <Header as="h4">Conclusão</Header>
          {conclusao.resumo && (
            <div className="br-message info" style={{ marginBottom: '1rem' }}>
              <div className="content">
                <p className="message-title">Resumo</p>
                <p className="message-body">{conclusao.resumo}</p>
              </div>
            </div>
          )}
          {conclusao.texto && (
            <p style={{ textAlign: 'justify', lineHeight: '1.7', color: '#333' }}>{conclusao.texto}</p>
          )}
        </>
      )}
    </div>
  );
};

const MemoriaisRSCPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [openIndex, setOpenIndex] = useState(-1);
  const [busca, setBusca] = useState('');
  const [query, setQuery] = useState('');
  const [pageSize, setPageSize] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setQuery(busca.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setOpenIndex(-1);
    axios
      .get(API_URL, { params: { page, ...(query ? { query } : {}) } })
      .then((res) => {
        setData(res.data);
        if (res.data.results?.length) {
          setPageSize((prev) => Math.max(prev || 0, res.data.results.length));
        }
      })
      .catch(() => setError('Não foi possível carregar os memoriais. Tente novamente mais tarde.'))
      .finally(() => setLoading(false));
  }, [page, query]);

  const totalPages = data ? Math.ceil(data.count / (pageSize || data.results?.length || 10)) : 1;

  const handleToggle = (idx) => setOpenIndex(openIndex === idx ? -1 : idx);

  const resultadosFiltrados = data?.results ?? [];

  return (
    <Segment basic className="content-area">
      <main>
        <Container style={{ paddingBottom: '4rem' }}>
          {/* Cabeçalho */}
          <div style={{ borderBottom: '3px solid #1351b4', paddingBottom: '0.5rem', marginBottom: '2rem' }}>
            <Header as="h1" style={{ color: '#1351b4', marginBottom: '0.25rem' }}>
              <i className="fas fa-file-alt" style={{ marginRight: '0.5rem' }} />
              Memoriais Descritivos RSC — TAE
            </Header>
            <p style={{ color: '#555', margin: 0 }}>
              Relação de memoriais descritivos com a descrição da trajetória profissional e individual do servidor desenvolvida ao longo da carreira, resultante da atuação profissional na dinâmica de ensino, de pesquisa e de extensão, e que demonstre os saberes, as competências e as experiências relacionados ao nível de RSC-PCCTAE pleiteado.
            </p>
          </div>

          {/* Filtro */}
          <div className="br-input" style={{ marginBottom: '1rem' }}>
            <label htmlFor="filtro-memorial">Buscar</label>
            <div className="input-content">
              <input
                id="filtro-memorial"
                type="text"
                placeholder="Filtrar por nome ou matrícula SIAPE..."
                value={busca}
                onChange={(e) => { setBusca(e.target.value); setOpenIndex(-1); }}
              />
              <button
                className="br-button circle small"
                type="button"
                aria-label="Buscar"
                style={{ pointerEvents: 'none' }}
              >
                <i className="fas fa-search" aria-hidden="true" />
              </button>
              {busca && (
                <button
                  className="br-button circle small"
                  type="button"
                  aria-label="Limpar busca"
                  onClick={() => { setBusca(''); setOpenIndex(-1); }}
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <Segment basic textAlign="center" style={{ padding: '4rem 0' }}>
              <Loader active inline="centered" size="large">Carregando memoriais...</Loader>
            </Segment>
          )}

          {/* Erro */}
          {error && !loading && (
            <div className="br-message danger">
              <div className="icon">
                <i className="fas fa-times-circle fa-lg" aria-hidden="true" />
              </div>
              <div className="content">
                <p className="message-title">Erro ao carregar</p>
                <p className="message-body">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Contador */}
              <p style={{ color: '#555', fontSize: '0.9em', marginBottom: '1rem' }}>
                <i className="fas fa-list" style={{ marginRight: '0.4rem' }} />
                {query
                  ? `${data.count} resultado(s) para "${query}"`
                  : `${data.count} memorial(is) encontrado(s)`}
              </p>

              {/* Sem resultados */}
              {resultadosFiltrados.length === 0 && (
                <div className="br-message info">
                  <div className="icon">
                    <i className="fas fa-search fa-lg" aria-hidden="true" />
                  </div>
                  <div className="content">
                    <p className="message-body">Nenhum memorial encontrado para "{busca}".</p>
                  </div>
                </div>
              )}

              {/* Accordion principal */}
              <div className="br-accordion">
                {resultadosFiltrados.map((item, idx) => (
                  <div className="item" key={item.id}>
                    <button
                      className="header"
                      type="button"
                      aria-expanded={openIndex === idx}
                      onClick={() => handleToggle(idx)}
                    >
                      <span className="icon">
                        <i className="fas fa-angle-down fa-lg" aria-hidden="true" />
                      </span>
                      <span className="title" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', width: '100%' }}>
                        <span style={{ fontWeight: 'bold', color: '#1351b4', flex: 1, minWidth: '10rem' }}>
                          {item.identificacao?.nome}
                        </span>
                        <span style={{ fontSize: '0.82em', color: '#555' }}>
                          SIAPE: {item.identificacao?.siape}
                        </span>
                        <span style={{ fontSize: '0.82em', color: '#555' }}>
                          {item.identificacao?.cargo}
                        </span>
                        <span style={{ fontSize: '0.82em', color: '#555' }}>
                          {item.identificacao?.lotacao}
                        </span>
                        <BRBadge color={nivelBadgeColor(item.requerimento?.nivel_rsc_pretendido)}>
                          {item.requerimento?.nivel_rsc_pretendido}
                        </BRBadge>
                      </span>
                    </button>
                    <div className="content" style={{ display: openIndex === idx ? 'block' : 'none' }}>
                      <div className="container" style={{ fontSize: '1rem', padding: '1rem 0', maxWidth: '100%' }}>
                        {openIndex === idx && <MemorialContent item={item} />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="br-pagination" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                  <button
                    className="br-button circle"
                    type="button"
                    aria-label="Página anterior"
                    disabled={!data.previous}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <i className="fas fa-angle-left" aria-hidden="true" />
                  </button>
                  <span style={{ margin: '0 1rem', color: '#555', lineHeight: '40px' }}>
                    Página {page} de {totalPages}
                  </span>
                  <button
                    className="br-button circle"
                    type="button"
                    aria-label="Próxima página"
                    disabled={!data.next}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <i className="fas fa-angle-right" aria-hidden="true" />
                  </button>
                </div>
              )}
            </>
          )}
        </Container>
      </main>
    </Segment>
  );
};

export default MemoriaisRSCPage;
